import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { ArrowLeftRight, PlusIcon } from '@repo/ui/icons'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import DynamicDataTable from '../../components/elements/dynamic-table'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { Transfer } from '../../lib/types/models/transfer/transfer'
import { getTranslatedName } from '../../lib/utils/functions/translation.functions'

export const transferColumns: Column<Transfer>[] = [
  {
    accessorKey: 'transferDate',
    header: 'Date du transfert',
    cell: ({ row: { original: transfer } }) => (
      <Badge text={DateFormatter.DDMMYYYY(transfer.transferDate)} variant="grey" />
    )
  },
  {
    accessorKey: 'transportMeans.name',
    header: 'Moyen de transport',
    cell: ({ row: { original: transfer } }) => getTranslatedName(transfer.transportMeans)
  },
  {
    accessorKey: 'tripProvider.companyName',
    header: 'Prestataire voyage',
    cell: ({ row: { original: transfer } }) => transfer.tripProvider?.companyName ?? '-'
  },
  {
    accessorKey: 'reservationNumber',
    header: 'N° résa',
    cell: ({ row: { original: transfer } }) => transfer.reservationNumber ?? '-'
  },
  {
    accessorKey: 'numberOfStudents',
    header: '# de jeunes'
  },
  {
    accessorKey: 'numberOfStaff',
    header: "# d'encadrants"
  },
  {
    accessorKey: 'departurePlace',
    header: 'Lieu de départ',
    cell: ({ row: { original: transfer } }) => (
      <div className="flex flex-row gap-2">
        <CountryFlag size="large" possibleCountry={transfer.departurePlace?.country} />
        {transfer.departurePlace?.city}
      </div>
    )
  },
  {
    accessorKey: 'departureTime',
    header: 'Heure de départ',
    cell: ({ row: { original: transfer } }) => (
      <Badge
        text={
          transfer.departureTime ? DateFormatter.getFormattedTime(transfer.departureTime) : '-'
        }
      />
    )
  },
  {
    accessorKey: 'arrivalPlace',
    header: "Lieu d'arrivée",
    cell: ({ row: { original: transfer } }) => (
      <div className="flex flex-row gap-2">
        <CountryFlag size="large" possibleCountry={transfer.arrivalPlace?.country} />
        {transfer.arrivalPlace?.city}
      </div>
    )
  },
  {
    accessorKey: 'arrivalTime',
    header: "Heure d'arrivée",
    cell: ({ row: { original: transfer } }) => (
      <Badge
        text={transfer.arrivalTime ? DateFormatter.getFormattedTime(transfer.arrivalTime) : '-'}
      />
    )
  },
  {
    accessorKey: 'totalPrice',
    header: 'Prix',
    cell: ({ row: { original: transfer } }) => (
      <Badge variant="primary" text={transfer.totalPrice ? `${transfer.totalPrice} €` : '-'} />
    )
  },
  {
    accessorKey: 'travelCompany.name',
    header: 'Compagnie de voyage',
    cell: ({ row: { original: transfer } }) => transfer.travelCompany?.name ?? '-'
  },
  {
    accessorKey: 'lecResponsable',
    header: 'Responsable LEC',
    cell: ({ row: { original: transfer } }) =>
      transfer.lecResponsable
        ? toFullName(transfer.lecResponsable?.firstName, transfer.lecResponsable?.lastName)
        : '-'
  }
]

const TransferListPage = () => {
  const { navigate, navigateWithEvent } = useNavigator()

  return (
    <div>
      <UserNavBar title={'Transferts'} icon={ArrowLeftRight} />
      <div className={'h-4'} />

      <DynamicDataTable<Transfer, null>
        title={'Tous les transferts'}
        deleteMessage={{ name: 'transfert', attributeName: 'id' }}
        columns={transferColumns}
        selectable
        allowSearchParam
        onRowClick={(transfer, event) =>
          navigateWithEvent(
            Paths.TRANSFER_UPDATE_PAGE.replace(':id', transfer.id.toString()),
            event
          )
        }
        endpoint={API.transfer.getAll.url}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Créer un transfert',
            fn: () => navigate(Paths.TRANSFER_CREATE_PAGE)
          }
        ]}
      />
    </div>
  )
}

export default TransferListPage
